import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, aboutUsApi, fileUploadRequest, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

function AdminAboutUsPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [activeTab, setActiveTab] = useState('mission'); // 'mission', 'gallery', 'banner', 'team'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    headerTitle: 'About Us',
    headerSubtitle: 'About',
    headerBgImage: '/assets/images/backgrounds/slider-2-2.jpg',
    tagline: 'About PsychMitra',
    title: 'Our Mission & Vision',
    introText: 'PsychMitra is dedicated to providing accessible, high-quality mental health services to everyone who needs them.',
    missionTitle: 'Our Mission',
    missionDescription: 'To create an innovative ecosystem connecting mental health professionals with clients, ensuring accessible, personalized, and sustainable care.',
    visionTitle: 'Our Vision',
    visionDescription: 'A world where mental health support is accessible, affordable, and empowering for everyone, and where professionals practice with dignity and autonomy.',
    highlightQuote: 'Our goal is simple yet powerful: to increase accessibility for those seeking care, and to create a fair, supportive space for those offering it.',
    ctaText: 'Get In Touch',
    ctaLink: '/contact',
    galleryImages: [],
    showTeamSection: false,
    teamTagline: 'Our Team',
    teamTitle: 'Meet Our Experts',
    teamMembers: [],
  });

  // Gallery Upload State
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [galleryUploadError, setGalleryUploadError] = useState('');

  // Banner Upload State
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerUploadError, setBannerUploadError] = useState('');

  // Team Member Modal State
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [teamModalMode, setTeamModalMode] = useState('create'); // 'create' | 'edit'
  const [editingTeamIndex, setEditingTeamIndex] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', role: '', image: '', bio: '' });
  const [uploadingTeamImage, setUploadingTeamImage] = useState(false);

  // Notification Toast
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4500);
  };

  // Fetch Live About Us Data
  const fetchAboutData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await aboutUsApi.getAboutUs();
      if (res?.success && res.data) {
        setFormData(res.data);
      }
    } catch (err) {
      console.error('Error loading about us data:', err);
      showFeedback('error', err.message || 'Failed to load About Us content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchAboutData();
  }, [fetchAboutData, authSession?.token, navigate]);

  // Handle Save All Changes
  const handleSaveChanges = async (e) => {
    if (e) e.preventDefault();
    try {
      setSaving(true);
      const res = await aboutUsApi.adminUpdateAboutUs(formData);
      if (res?.success) {
        setFormData(res.data);
        showFeedback('success', 'About Us content updated and published live!');
      }
    } catch (err) {
      console.error('Error saving about us content:', err);
      showFeedback('error', err.message || 'Failed to update About Us content');
    } finally {
      setSaving(false);
    }
  };

  // Handle Reset to Default Template
  const handleResetToDefault = async () => {
    if (!window.confirm('Are you sure you want to reset the About Us page to its default layout and text?')) {
      return;
    }
    try {
      setResetting(true);
      const res = await aboutUsApi.adminResetAboutUs();
      if (res?.success) {
        setFormData(res.data);
        showFeedback('success', 'About Us page restored to default template!');
      }
    } catch (err) {
      console.error('Error resetting about us content:', err);
      showFeedback('error', err.message || 'Failed to reset About Us content');
    } finally {
      setResetting(false);
    }
  };

  // Gallery Image Upload Handler
  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setGalleryUploadError('');
    setUploadingGallery(true);

    try {
      const newUrls = [];
      for (let i = 0; i < files.length; i++) {
        const data = new FormData();
        data.append('file', files[i]);
        const res = await fileUploadRequest('/upload/file', data);
        if (res?.url) {
          newUrls.push(res.url);
        }
      }

      setFormData((prev) => ({
        ...prev,
        galleryImages: [...prev.galleryImages, ...newUrls],
      }));
      showFeedback('success', `${newUrls.length} image(s) added to gallery! Remember to click Save Changes.`);
    } catch (err) {
      console.error('Gallery upload error:', err);
      setGalleryUploadError(err.message || 'Failed to upload gallery image');
    } finally {
      setUploadingGallery(false);
      e.target.value = '';
    }
  };

  // Remove Gallery Image
  const handleRemoveGalleryImage = (idxToRemove) => {
    setFormData((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, idx) => idx !== idxToRemove),
    }));
  };

  // Banner Image Upload Handler
  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBannerUploadError('');
    setUploadingBanner(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setFormData((prev) => ({ ...prev, headerBgImage: res.url }));
        showFeedback('success', 'Header background banner updated!');
      }
    } catch (err) {
      console.error('Banner upload error:', err);
      setBannerUploadError(err.message || 'Failed to upload header background banner');
    } finally {
      setUploadingBanner(false);
      e.target.value = '';
    }
  };

  // Open Team Modal (Create / Edit)
  const handleOpenTeamModal = (mode, index = null) => {
    setTeamModalMode(mode);
    setEditingTeamIndex(index);
    if (mode === 'edit' && index !== null) {
      setTeamForm({ ...formData.teamMembers[index] });
    } else {
      setTeamForm({ name: '', role: '', image: '', bio: '' });
    }
    setIsTeamModalOpen(true);
  };

  // Team Member Photo Upload Handler
  const handleTeamPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTeamImage(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setTeamForm((prev) => ({ ...prev, image: res.url }));
      }
    } catch (err) {
      console.error('Team photo upload error:', err);
      alert('Failed to upload team photo: ' + err.message);
    } finally {
      setUploadingTeamImage(false);
      e.target.value = '';
    }
  };

  // Save Team Member to state
  const handleSaveTeamMember = (e) => {
    e.preventDefault();
    if (!teamForm.name.trim() || !teamForm.role.trim()) {
      alert('Name and Role are required.');
      return;
    }

    setFormData((prev) => {
      const updated = [...(prev.teamMembers || [])];
      if (teamModalMode === 'create') {
        updated.push(teamForm);
      } else if (editingTeamIndex !== null) {
        updated[editingTeamIndex] = teamForm;
      }
      return { ...prev, teamMembers: updated };
    });

    setIsTeamModalOpen(false);
    showFeedback('success', `Team member ${teamModalMode === 'create' ? 'added' : 'updated'}! Click Save Changes to publish.`);
  };

  // Delete Team Member from state
  const handleDeleteTeamMember = (idxToDelete) => {
    if (!window.confirm('Delete this team member?')) return;
    setFormData((prev) => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((_, idx) => idx !== idxToDelete),
    }));
  };

  return (
    <div className="admin-layout" style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <DashboardSidebar userRole="admin" />

      <div style={{ flex: 1, marginLeft: '260px', padding: '32px', maxWidth: '1400px', width: 'calc(100% - 260px)' }}>
        {/* Top Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <span style={{ fontSize: '28px' }}>ℹ️</span>
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                About Us Content Management
              </h1>
            </div>
            <p style={{ margin: 0, color: '#64748b', fontSize: '15px' }}>
              Customize and manage all content, mission & vision, gallery visual slides, and team sections on your public About page.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a
              href="/about"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: '#f1f5f9',
                color: '#334155',
                border: '1px solid #cbd5e1',
                padding: '10px 18px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>👁️</span>
              <span>Preview Live Page</span>
            </a>

            <button
              onClick={handleResetToDefault}
              disabled={resetting}
              style={{
                background: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
                padding: '10px 18px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '13px',
                cursor: resetting ? 'not-allowed' : 'pointer',
              }}
            >
              {resetting ? 'Resetting...' : '🔄 Reset to Default'}
            </button>

            <button
              onClick={handleSaveChanges}
              disabled={saving}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>{saving ? '⏳' : '💾'}</span>
              <span>{saving ? 'Saving Live...' : 'Save & Publish Changes'}</span>
            </button>
          </div>
        </div>

        {/* Feedback Alert */}
        {feedback.message && (
          <div
            style={{
              padding: '14px 18px',
              borderRadius: '10px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: feedback.type === 'success' ? '#ecfdf5' : '#fef2f2',
              color: feedback.type === 'success' ? '#047857' : '#b91c1c',
              border: `1px solid ${feedback.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{feedback.type === 'success' ? '✅' : '⚠️'}</span>
              <span>{feedback.message}</span>
            </div>
            <button
              onClick={() => setFeedback({ type: '', message: '' })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'inherit' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Section Navigation Tabs */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', flexWrap: 'wrap' }}>
          {[
            { key: 'mission', label: '🎯 Mission, Vision & Core Content', icon: '🎯' },
            { key: 'gallery', label: `🖼️ Visual Gallery (${formData.galleryImages?.length || 0} Images)`, icon: '🖼️' },
            { key: 'banner', label: '🌄 Header Banner', icon: '🌄' },
            { key: 'team', label: `👥 Experts & Team (${formData.teamMembers?.length || 0})`, icon: '👥' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer',
                background: activeTab === tab.key ? '#4f46e5' : '#ffffff',
                color: activeTab === tab.key ? '#ffffff' : '#475569',
                boxShadow: activeTab === tab.key ? '0 4px 12px rgba(79, 70, 229, 0.25)' : '0 1px 3px rgba(0,0,0,0.03)',
                transition: 'all 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', color: '#64748b' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <div style={{ fontSize: '16px', fontWeight: 600 }}>Loading About Us Content...</div>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            {/* ==================== TAB 1: MISSION, VISION & CORE CONTENT ==================== */}
            {activeTab === 'mission' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Section Tagline
                    </label>
                    <input
                      type="text"
                      value={formData.tagline}
                      onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                      placeholder="e.g. About PsychMitra"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Main Section Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g. Our Mission & Vision"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Introduction Paragraph
                  </label>
                  <textarea
                    rows={3}
                    value={formData.introText}
                    onChange={(e) => setFormData({ ...formData, introText: e.target.value })}
                    placeholder="Brief intro about PsychMitra organization and goals..."
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontFamily: 'inherit' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  {/* Mission */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <span style={{ fontSize: '20px' }}>🎯</span>
                      <input
                        type="text"
                        value={formData.missionTitle}
                        onChange={(e) => setFormData({ ...formData, missionTitle: e.target.value })}
                        style={{ fontWeight: 800, fontSize: '16px', color: '#0f172a', border: 'none', background: 'transparent', borderBottom: '1px dashed #cbd5e1', padding: '2px 4px', width: '100%' }}
                      />
                    </div>
                    <textarea
                      rows={4}
                      value={formData.missionDescription}
                      onChange={(e) => setFormData({ ...formData, missionDescription: e.target.value })}
                      placeholder="Enter mission description..."
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: 'white', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* Vision */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <span style={{ fontSize: '20px' }}>🌟</span>
                      <input
                        type="text"
                        value={formData.visionTitle}
                        onChange={(e) => setFormData({ ...formData, visionTitle: e.target.value })}
                        style={{ fontWeight: 800, fontSize: '16px', color: '#0f172a', border: 'none', background: 'transparent', borderBottom: '1px dashed #cbd5e1', padding: '2px 4px', width: '100%' }}
                      />
                    </div>
                    <textarea
                      rows={4}
                      value={formData.visionDescription}
                      onChange={(e) => setFormData({ ...formData, visionDescription: e.target.value })}
                      placeholder="Enter vision description..."
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: 'white', fontFamily: 'inherit' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Highlight Quote / Core Philosophy (Green callout box)
                  </label>
                  <textarea
                    rows={2}
                    value={formData.highlightQuote}
                    onChange={(e) => setFormData({ ...formData, highlightQuote: e.target.value })}
                    placeholder="Highlight quote displayed prominently in the about card..."
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontFamily: 'inherit' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Call to Action Button Text
                    </label>
                    <input
                      type="text"
                      value={formData.ctaText}
                      onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
                      placeholder="e.g. Get In Touch"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Button Link / Destination URL
                    </label>
                    <input
                      type="text"
                      value={formData.ctaLink}
                      onChange={(e) => setFormData({ ...formData, ctaLink: e.target.value })}
                      placeholder="e.g. /contact or #contact"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ==================== TAB 2: VISUAL GALLERY ==================== */}
            {activeTab === 'gallery' && (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                    Interactive Visual Gallery Images
                  </h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                    The first image acts as the primary featured visual, while the remaining images appear as interactive thumbnails that swap on click.
                  </p>
                </div>

                {/* Upload Box */}
                <div style={{ marginBottom: '24px' }}>
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px',
                      background: uploadingGallery ? '#f1f5f9' : '#fafafa',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '12px',
                      cursor: uploadingGallery ? 'not-allowed' : 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <input
                      type="file"
                      multiple
                      accept="image/*,.jpg,.jpeg,.png,.webp,.svg"
                      disabled={uploadingGallery}
                      onChange={handleGalleryUpload}
                      style={{ display: 'none' }}
                    />
                    <span style={{ fontSize: '32px', marginBottom: '6px' }}>{uploadingGallery ? '⏳' : '📤'}</span>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#334155' }}>
                      {uploadingGallery ? 'Uploading gallery image(s)...' : 'Click or Drag Images to Upload to Gallery'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                      Supports multiple images (PNG, JPG, WEBP, SVG)
                    </span>
                  </label>
                  {galleryUploadError && (
                    <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', fontWeight: 600 }}>
                      ⚠️ {galleryUploadError}
                    </div>
                  )}
                </div>

                {/* Gallery Images List */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '18px' }}>
                  {formData.galleryImages?.map((imgUrl, idx) => (
                    <div
                      key={idx}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: '#f8fafc',
                        position: 'relative',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                      }}
                    >
                      <div style={{ height: '140px', background: '#e2e8f0', position: 'relative' }}>
                        <img
                          src={getMediaUrl(imgUrl)}
                          alt={`Gallery ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.src = '/assets/images/backgrounds/slider-2-2.jpg'; }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            background: idx === 0 ? '#4f46e5' : 'rgba(15, 23, 42, 0.75)',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: '4px',
                          }}
                        >
                          {idx === 0 ? '⭐ Primary Featured' : `Slide #${idx + 1}`}
                        </span>
                      </div>

                      <div style={{ padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                          {imgUrl.split('/').pop()}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveGalleryImage(idx)}
                          style={{
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ==================== TAB 3: HEADER BANNER ==================== */}
            {activeTab === 'banner' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Page Header Banner Title
                    </label>
                    <input
                      type="text"
                      value={formData.headerTitle}
                      onChange={(e) => setFormData({ ...formData, headerTitle: e.target.value })}
                      placeholder="e.g. About Us"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Breadcrumb Subtitle
                    </label>
                    <input
                      type="text"
                      value={formData.headerSubtitle}
                      onChange={(e) => setFormData({ ...formData, headerSubtitle: e.target.value })}
                      placeholder="e.g. About"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Header Background Image Banner
                  </label>

                  <div style={{ display: 'flex', gap: '20px', alignItems: 'center', background: '#f8fafc', padding: '18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <img
                      src={getMediaUrl(formData.headerBgImage)}
                      alt="Banner Preview"
                      style={{ width: '220px', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      onError={(e) => { e.target.src = '/assets/images/backgrounds/slider-2-2.jpg'; }}
                    />

                    <div>
                      <label
                        style={{
                          display: 'inline-block',
                          background: '#4f46e5',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 700,
                          cursor: uploadingBanner ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          disabled={uploadingBanner}
                          onChange={handleBannerUpload}
                          style={{ display: 'none' }}
                        />
                        {uploadingBanner ? '⏳ Uploading Banner...' : '📤 Replace Banner Image'}
                      </label>
                      <span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                        Recommended resolution: 1920x450px
                      </span>
                    </div>
                  </div>
                  {bannerUploadError && (
                    <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', fontWeight: 600 }}>
                      ⚠️ {bannerUploadError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ==================== TAB 4: EXPERTS & TEAM SECTION ==================== */}
            {activeTab === 'team' && (
              <div>
                {/* Enable / Disable Toggle */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '18px 24px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                      Display "Meet Our Experts" Team Section
                    </h3>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
                      Toggle whether the team/experts showcase grid is visible at the bottom of the About page.
                    </p>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.showTeamSection}
                      onChange={(e) => setFormData({ ...formData, showTeamSection: e.target.checked })}
                      style={{ width: '22px', height: '22px', accentColor: '#4f46e5', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: 700, fontSize: '14px', color: formData.showTeamSection ? '#16a34a' : '#94a3b8' }}>
                      {formData.showTeamSection ? 'Visible on Page' : 'Hidden'}
                    </span>
                  </label>
                </div>

                {/* Team Titles */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Team Section Tagline
                    </label>
                    <input
                      type="text"
                      value={formData.teamTagline}
                      onChange={(e) => setFormData({ ...formData, teamTagline: e.target.value })}
                      placeholder="e.g. Our Team"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Team Section Title
                    </label>
                    <input
                      type="text"
                      value={formData.teamTitle}
                      onChange={(e) => setFormData({ ...formData, teamTitle: e.target.value })}
                      placeholder="e.g. Meet Our Experts"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                </div>

                {/* Team Members Header & Add Button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                    Team Members List ({formData.teamMembers?.length || 0})
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleOpenTeamModal('create')}
                    style={{
                      background: '#4f46e5',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span>➕</span>
                    <span>Add Member</span>
                  </button>
                </div>

                {/* Members Grid */}
                {formData.teamMembers?.length === 0 ? (
                  <div style={{ padding: '30px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', color: '#64748b' }}>
                    No team members added yet. Click "+ Add Member" to add leaders or experts.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
                    {formData.teamMembers?.map((member, idx) => (
                      <div
                        key={idx}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          padding: '16px',
                          background: '#f8fafc',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          gap: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                          <img
                            src={getMediaUrl(member.image) || '/assets/images/team/team-1-1.jpg'}
                            alt={member.name}
                            style={{ width: '54px', height: '54px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #cbd5e1' }}
                            onError={(e) => { e.target.src = '/assets/images/team/team-1-1.jpg'; }}
                          />
                          <div>
                            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '15px' }}>{member.name}</div>
                            <div style={{ fontSize: '12px', color: '#4f46e5', fontWeight: 600 }}>{member.role}</div>
                          </div>
                        </div>

                        {member.bio && (
                          <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: 1.4 }}>
                            {member.bio}
                          </p>
                        )}

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenTeamModal('edit', idx)}
                            style={{ background: '#e0e7ff', color: '#3730a3', border: 'none', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteTeamMember(idx)}
                            style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== TEAM MEMBER CREATE / EDIT MODAL ==================== */}
        {isTeamModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                maxWidth: '520px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              }}
            >
              <div
                style={{
                  padding: '18px 24px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                  {teamModalMode === 'create' ? '➕ Add Team Member' : '✏️ Edit Team Member'}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsTeamModalOpen(false)}
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSaveTeamMember} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Dr. John Doe"
                    value={teamForm.name}
                    onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Role / Title *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Clinical Psychologist"
                    value={teamForm.role}
                    onChange={(e) => setTeamForm({ ...teamForm, role: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Member Photo (Upload)
                  </label>
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                    <img
                      src={getMediaUrl(teamForm.image) || '/assets/images/team/team-1-1.jpg'}
                      alt="Preview"
                      style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #cbd5e1' }}
                      onError={(e) => { e.target.src = '/assets/images/team/team-1-1.jpg'; }}
                    />
                    <label
                      style={{
                        background: '#f1f5f9',
                        color: '#334155',
                        padding: '8px 14px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: uploadingTeamImage ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingTeamImage}
                        onChange={handleTeamPhotoUpload}
                        style={{ display: 'none' }}
                      />
                      {uploadingTeamImage ? '⏳ Uploading...' : '📷 Upload Photo'}
                    </label>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Bio / Short Description (Optional)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Short summary about their credentials and specialty..."
                    value={teamForm.bio}
                    onChange={(e) => setTeamForm({ ...teamForm, bio: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontFamily: 'inherit' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                  <button
                    type="button"
                    onClick={() => setIsTeamModalOpen(false)}
                    style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{ padding: '8px 20px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {teamModalMode === 'create' ? 'Add Member' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminAboutUsPage;
